import { getHotkeyRegistry } from 'javascripts/keybindings/HotkeyRegistryInstance';
import { parseHotkey, IS_MAC } from '@aha-app/is-hotkey';
import { activateTabContainingElement } from 'javascripts/tab_helpers';

// This is the API for adding and removing hotkey handlers
// It also generates consistent lablels for the hotkeys
// to be used in tooltips/placeholders/etc

function translateKey(key, options = {}) {
  const { isMac } = { isMac: IS_MAC, ...options };
  let translatedKey = key;
  if (isMac) {
    switch (key) {
      case 'tab':
        translatedKey = '⇥';
        break;
      case 'enter':
        translatedKey = '↩';
        break;
      case 'escape':
        translatedKey = 'Esc';
        break;
      default:
        translatedKey = key.toUpperCase();
        break;
    }
  } else {
    switch (key) {
      case 'tab':
        translatedKey = 'Tab';
        break;
      case 'enter':
        translatedKey = 'Enter';
        break;
      case 'escape':
        translatedKey = 'Esc';
        break;
      default:
        translatedKey = key.toUpperCase();
        break;
    }
  }

  return translatedKey;
}

// Given a hotkey it will return it's label properly formatted for
// the current OS.
function hotkeyLabel(hotkey, options) {
  const { isMac } = { isMac: IS_MAC, ...options };
  const { separator } = { separator: IS_MAC ? '' : '+', ...options };
  const parsedHotkey =
    typeof hotkey === 'string' ? parseHotkey(hotkey, { byKey: true }) : hotkey;
  let label = [];
  if (isMac) {
    if (parsedHotkey.ctrlKey) {
      label.push('Ctrl');
    }

    if (parsedHotkey.altKey) {
      label.push('⌥');
    }

    if (parsedHotkey.shiftKey) {
      // Mac has a special "backtab" indicator
      if (parsedHotkey.key !== 'tab') {
        label.push('⇧');
      }
    }

    if (parsedHotkey.metaKey) {
      label.push('⌘');
    }

    if (parsedHotkey.shiftKey && parsedHotkey.key === 'tab') {
      label.push('⇤');
    } else {
      label.push(translateKey(parsedHotkey.key, options));
    }
  } else {
    label = [];
    if (parsedHotkey.ctrlKey || parsedHotkey.metaKey) {
      label.push('Ctrl');
    }

    if (parsedHotkey.altKey) {
      label.push('Alt');
    }

    if (parsedHotkey.shiftKey) {
      label.push('Shift');
    }

    label.push(translateKey(parsedHotkey.key, options));
  }
  return label.join(separator);
}

// Remove your handler function when done
function unbindHotkey(hotkey) {
  const registry = getHotkeyRegistry();

  registry.removeHandler(hotkey);
}

// Binds a callback to a hotkey (this will generate an error if
// the hotkey is not defined in the registry (see registerHotkeys.js) and
// returns properly formatted string representations of the hotkey
//
// Input options:
//   title: The name of the hotkey used in tooltips etc
//   target: CSS selector for the DOM element associated with the hotkey
//
//   If the title and target are given the hotkey will be appended to the
//   title and a tooltop will be added to the DOM element
//
// Returns:
//   label: A string with the formatted hotkey for the current OS
//   title: The inputted title with the hotkey label embedded
//          e.g. "Save (⌘S)"
//
export default function bindHotkey(hotkey, callback, options) {
  const registry = getHotkeyRegistry();
  const label = hotkeyLabel(hotkey);
  let { title, target, placement } = { title: '', ...options };

  // construct the label
  title = `${title} (${label})`;

  // add a tooltip to the element if passed
  if (target) {
    let targetElem = document.querySelector(target);
    if (targetElem) {
      // if the target is in a react in place wrapper attach to that instead
      // otherwise we are modifying a react component on the fly which can lead to issues
      if (
        targetElem.parentNode &&
        targetElem.parentNode.classList.contains('react-in-place-wrapper')
      ) {
        targetElem = targetElem.parentNode;
      }

      if (targetElem.dataset.cssOnlyTooltip) {
        targetElem.dataset.cssOnlyTooltip = title;
      } else {
        targetElem.dataset.title = title;
        if (placement) {
          targetElem.dataset.placement = placement;
        }
      }

      const prevCallback = callback;
      callback = (...args) => {
        activateTabContainingElement(targetElem);

        return prevCallback(...args);
      };
    }
  }

  // add the handler
  registry.addHandler(hotkey, callback);

  return {
    label: label,
    title: title,
  };
}

export { bindHotkey, hotkeyLabel, unbindHotkey };
